import React from 'react';
import type { ReactNode } from 'react';
import type { ToolType } from '../types/dashboard';
import { usePermissions } from '../hooks/usePermissions';
import { useUpgradeAction } from '../hooks/useUpgradeAction';

interface PermissionGateProps {
  toolType: ToolType;
  children: ReactNode;
  onRemove?: () => void;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({ toolType, children, onRemove }) => {
  const { getToolAccessInfo } = usePermissions();
  const accessInfo = getToolAccessInfo(toolType);

  const { onUpgrade, isRedirecting } = useUpgradeAction(!accessInfo.hasAccess);

  // If user has access, render children normally
  if (accessInfo.hasAccess) {
    return <>{children}</>;
  }

  // No access: render the upgrade card ALONE — children are NEVER instantiated.
  // Prevents the widget from mounting, fetching data, or subscribing to WebSockets.
  const permissionDisplayName = accessInfo.requiredPermission?.displayName;
  const permissionDescription = accessInfo.requiredPermission?.description;

  return (
    <div className="relative h-full bg-dark-surface rounded-lg overflow-hidden flex items-center justify-center">
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-1 right-1 z-20 text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors bg-dark-surface-light/80"
          title="Close widget"
        >
          ×
        </button>
      )}

      <div className="text-center px-6 py-8 max-w-md">
        <div className="mb-4">
          <svg
            className="w-16 h-16 mx-auto text-accent-yellow"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M2.5 19h19v2h-19v-2zm19.57-9.36c-.21-.8-1.04-1.28-1.84-1.06l-4.23 1.12-3.47-5.46a1.5 1.5 0 00-2.56 0L6.5 9.7l-4.23-1.12c-.8-.22-1.63.26-1.84 1.06-.21.8.26 1.63 1.06 1.84l4.53 1.2v6.32h12v-6.32l4.53-1.2c.8-.21 1.27-1.04 1.06-1.84z" />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-gray-100 mb-2">
          {accessInfo.toolConfig?.displayName} Locked
        </h3>

        <p className="text-sm text-gray-300 mb-1">
          This tool requires{' '}
          <span className="font-medium text-accent-yellow">
            {permissionDisplayName}
          </span>
        </p>

        {permissionDescription && (
          <p className="text-xs text-gray-400 mb-6">
            {permissionDescription}
          </p>
        )}

        <button
          onClick={onUpgrade}
          disabled={isRedirecting}
          className="inline-flex items-center gap-2 px-6 py-3 bg-bull-blue hover:bg-bull-blue/90 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          {isRedirecting ? 'Redirecting...' : 'Upgrade Now'}
        </button>
      </div>
    </div>
  );
};
